import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { X, Download, CheckCircle, XCircle, ZoomIn, ZoomOut, RotateCw, FileText } from 'lucide-react';
import api from '../../lib/api';

interface DocumentViewerProps {
  documentId: string;
  onClose: () => void;
}

interface DocumentDetails {
  id: string;
  type: string;
  fileName: string;
  fileUrl: string;
  mimeType: string;
  fileSize: number;
  uploadedAt: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  validationNotes: string | null;
  ocrData: Record<string, string> | null;
}

const documentTypeLabels: Record<string, string> = {
  LEGAL_OPINION: 'Parecer Jurídico',
  VALUATION_REPORT: 'Laudo de Avaliação',
  CUSTODY_PROOF: 'Comprovante de Custódia',
  OWNERSHIP_PROOF: 'Comprovante de Propriedade',
  REGULATORY_APPROVAL: 'Aprovação Regulatória',
  FINANCIAL_STATEMENTS: 'Demonstrações Financeiras',
  CONTRACT: 'Contrato',
  OTHER: 'Outro',
};

export function DocumentViewer({ documentId, onClose }: DocumentViewerProps) {
  const queryClient = useQueryClient();
  const [zoom, setZoom] = useState(100);
  const [rotation, setRotation] = useState(0);
  const [notes, setNotes] = useState('');

  const { data: document, isLoading } = useQuery({
    queryKey: ['document', documentId],
    queryFn: async () => {
      const res = await api.get(`/tokenization/documents/${documentId}`);
      return res.data as DocumentDetails;
    },
  });

  const approveDocument = useMutation({
    mutationFn: async () => {
      await api.post(`/tokenization/documents/${documentId}/approve`, { notes });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document', documentId] });
      queryClient.invalidateQueries({ queryKey: ['asset-review'] });
    },
  });

  const rejectDocument = useMutation({
    mutationFn: async () => {
      await api.post(`/tokenization/documents/${documentId}/reject`, { notes });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document', documentId] });
      queryClient.invalidateQueries({ queryKey: ['asset-review'] });
    },
  });

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const isImage = document?.mimeType?.startsWith('image/');
  const isPdf = document?.mimeType === 'application/pdf';

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
      <div className="bg-surface-900 rounded-xl w-full max-w-6xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-surface-800">
          <div>
            <h2 className="text-lg font-semibold">
              {document ? documentTypeLabels[document.type] || document.type : 'Carregando...'}
            </h2>
            {document && (
              <p className="text-sm text-surface-400">{document.fileName}</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {document && (
              <>
                <button
                  onClick={() => setZoom(Math.max(50, zoom - 25))}
                  className="p-2 hover:bg-surface-800 rounded-lg transition-colors"
                  title="Diminuir zoom"
                >
                  <ZoomOut className="w-5 h-5" />
                </button>
                <span className="text-sm w-12 text-center">{zoom}%</span>
                <button
                  onClick={() => setZoom(Math.min(200, zoom + 25))}
                  className="p-2 hover:bg-surface-800 rounded-lg transition-colors"
                  title="Aumentar zoom"
                >
                  <ZoomIn className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setRotation((rotation + 90) % 360)}
                  className="p-2 hover:bg-surface-800 rounded-lg transition-colors"
                  title="Rotacionar"
                >
                  <RotateCw className="w-5 h-5" />
                </button>
                <a
                  href={document.fileUrl}
                  download={document.fileName}
                  className="p-2 hover:bg-surface-800 rounded-lg transition-colors"
                  title="Download"
                >
                  <Download className="w-5 h-5" />
                </a>
              </>
            )}
            <button
              onClick={onClose}
              className="p-2 hover:bg-surface-800 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 flex overflow-hidden">
          {/* Document Preview */}
          <div className="flex-1 overflow-auto p-4 bg-surface-950">
            {isLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : document ? (
              <div className="flex items-center justify-center min-h-full">
                {isImage ? (
                  <img
                    src={document.fileUrl}
                    alt={document.fileName}
                    className="max-w-full transition-transform"
                    style={{
                      transform: `scale(${zoom / 100}) rotate(${rotation}deg)`,
                    }}
                  />
                ) : isPdf ? (
                  <iframe
                    src={document.fileUrl}
                    className="w-full h-full min-h-[600px]"
                    title={document.fileName}
                  />
                ) : (
                  <div className="text-center">
                    <FileText className="w-16 h-16 text-surface-600 mx-auto mb-4" />
                    <p className="text-surface-400 mb-4">
                      Visualização não disponível para este tipo de arquivo
                    </p>
                    <a
                      href={document.fileUrl}
                      download={document.fileName}
                      className="px-4 py-2 bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors inline-flex items-center gap-2"
                    >
                      <Download className="w-4 h-4" />
                      Baixar Arquivo
                    </a>
                  </div>
                )}
              </div>
            ) : null}
          </div>

          {/* Sidebar */}
          <div className="w-80 border-l border-surface-800 p-4 overflow-auto">
            {document && (
              <div className="space-y-6">
                {/* Document Info */}
                <div>
                  <h3 className="text-sm font-medium text-surface-400 mb-2">Informações</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-surface-400">Tamanho</span>
                      <span>{formatFileSize(document.fileSize)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-surface-400">Enviado em</span>
                      <span>{formatDate(document.uploadedAt)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-surface-400">Status</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        document.status === 'APPROVED' ? 'bg-green-500/20 text-green-400' :
                        document.status === 'REJECTED' ? 'bg-red-500/20 text-red-400' :
                        'bg-yellow-500/20 text-yellow-400'
                      }`}>
                        {document.status === 'APPROVED' ? 'Aprovado' : 
                         document.status === 'REJECTED' ? 'Rejeitado' : 'Pendente'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* OCR Data */}
                {document.ocrData && Object.keys(document.ocrData).length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-surface-400 mb-2">Dados Extraídos (OCR)</h3>
                    <div className="space-y-2 text-sm bg-surface-800 rounded-lg p-3">
                      {Object.entries(document.ocrData).map(([key, value]) => (
                        <div key={key}>
                          <span className="text-surface-400">{key}:</span>
                          <span className="ml-2">{value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Validation Notes */}
                {document.validationNotes && (
                  <div>
                    <h3 className="text-sm font-medium text-surface-400 mb-2">Observações</h3>
                    <p className="text-sm bg-surface-800 rounded-lg p-3">{document.validationNotes}</p>
                  </div>
                )}

                {/* Actions */}
                {document.status === 'PENDING' && (
                  <div>
                    <h3 className="text-sm font-medium text-surface-400 mb-2">Ações</h3>
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Observações (opcional)"
                      className="w-full px-3 py-2 bg-surface-800 border border-surface-700 rounded-lg text-sm mb-3"
                      rows={3}
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={() => approveDocument.mutate()}
                        disabled={approveDocument.isPending}
                        className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 rounded-lg transition-colors text-sm"
                      >
                        <CheckCircle className="w-4 h-4" />
                        Aprovar
                      </button>
                      <button
                        onClick={() => rejectDocument.mutate()}
                        disabled={rejectDocument.isPending}
                        className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 rounded-lg transition-colors text-sm"
                      >
                        <XCircle className="w-4 h-4" />
                        Rejeitar
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default DocumentViewer;
